import { translate } from "./formatters";

import { ExtendedViewItem, Receipt } from "@/types/receipt";
import { ExtendedServicePayload, ServiceDesign } from "@/types/extendedViewService";
type ExtendedViewPosition = ExtendedViewItem["extended_view_position"];

export const getStoreName = (receipt: Receipt): string | null =>
  receipt.merchant?.purchase_location?.store_name ?? null;

export const getButtonText = (
  position: ExtendedViewPosition,
  lang: string,
  serviceDesign?: Partial<ServiceDesign>
): string => {
  // Use custom button text if available
  // button_text replaced by receipt_button_text
  if (serviceDesign?.receipt_button_text) {
    return serviceDesign.receipt_button_text;
  }
  const textMap: { [key: string]: { nor: string; eng: string } } = {
    website: { nor: "Besøk nettside", eng: "Visit website" },
    customer_club: { nor: "Kundeklubb", eng: "Customer club" },
    help_desk: { nor: "Kundestøtte", eng: "Customer support" },
    vat_refund: { nor: "MVA-refusjon", eng: "VAT refund" },
    financing: { nor: "Finansiering", eng: "Financing" },
    insurance: { nor: "Forsikring", eng: "Insurance" },
    second_hand: { nor: "Brukthandel", eng: "Second-hand" },
    add_ownership: { nor: "Registrer eierskap", eng: "Register ownership" },
    share_data: { nor: "Del data", eng: "Share data" },
    return_service: { nor: "Tilbakeleveransetjeneste", eng: "Return service" },
    zeipt_accounting: {
      nor: "Send kvitteringen til regnskap",
      eng: "Send receipt to accounting",
    },
    zeipt_second_hand: {
      nor: "Selg produktet på second hand",
      eng: "Sell your product on second hand",
    },
    zeipt_insurance_claims: {
      nor: "Start en forsikringssak",
      eng: "Start an insurance claim",
    },
  };

  const text = textMap[position];
  return text ? translate(lang, text.nor, text.eng) : position;
};

export const getActionableButtonText = (
  position: ExtendedViewPosition,
  lang: string,
  serviceDesign?: Partial<ServiceDesign>
): string => {
  // Use custom actionable button text if available
  // actionable_button_text replaced by extended_receipt_view_button_text
  if (serviceDesign?.extended_receipt_view_button_text) {
    return serviceDesign.extended_receipt_view_button_text;
  }
  const textMap: { [key: string]: { nor: string; eng: string } } = {
    customer_club: { nor: "Bli medlem", eng: "Become a member" },
    help_desk: { nor: "Kontakt kundestøtte", eng: "Contact support" },
    vat_refund: { nor: "Klikk for å kreve MVA", eng: "Click to claim VAT" },
    financing: { nor: "Søk om finansiering", eng: "Apply for financing" },
    insurance: { nor: "Kjøp forsikring", eng: "Purchase insurance" },
    second_hand: { nor: "Selg produktet", eng: "Sell product" },
    add_ownership: {
      nor: "Registrer eierskap nå",
      eng: "Register ownership now",
    },
    share_data: { nor: "Del mine data", eng: "Share my data" },
    zeipt_accounting: { nor: "Send til regnskap", eng: "Send to accounting" },
    zeipt_second_hand: {
      nor: "Selg på second hand",
      eng: "Sell on second hand",
    },
    zeipt_insurance_claims: {
      nor: "Start forsikringssak",
      eng: "Start insurance claim",
    },
  };

  const text = textMap[position];
  return text
    ? translate(lang, text.nor, text.eng)
    : translate(lang, "Åpne tjeneste", "Open service");
};

export const getActionText = (
  position: ExtendedViewItem["extended_view_position"],
  lang: string,
  serviceDesign?: Partial<ServiceDesign>
): string => {
  // Use custom action text if available
  // action_text replaced by service_view_button_text
  if (serviceDesign?.service_view_button_text) {
    return serviceDesign.service_view_button_text;
  }

  const actions: Record<
    ExtendedViewItem["extended_view_position"],
    { eng: string; nor: string }
  > = {
    website: { eng: "Visit Website", nor: "Besøk nettside" },
    customer_club: { eng: "Join Now", nor: "Bli medlem" },
    help_desk: { eng: "Get Help", nor: "Få hjelp" },
    vat_refund: { eng: "Click to claim VAT", nor: "Klikk for å kreve MVA" },
    financing: { eng: "Apply Now", nor: "Søk nå" },
    insurance: { eng: "Learn More", nor: "Lær mer" },
    second_hand: { eng: "View Market", nor: "Se marked" },
    add_ownership: { eng: "Register Product", nor: "Registrer produkt" },
    share_data: { eng: "Manage Data", nor: "Behandle data" },
    return_service: { eng: "Start Return", nor: "Start retur" },
    zeipt_accounting: { eng: "Send to accounting", nor: "Send til regnskap" },
    zeipt_second_hand: {
      eng: "Sell on second hand",
      nor: "Selg på second hand",
    },
    zeipt_insurance_claims: {
      eng: "Start insurance claim",
      nor: "Start forsikringssak",
    },
  };

  return lang === "nor" ? actions[position].nor : actions[position].eng;
};

export const getSharingWarningText = (
  position: ExtendedViewPosition,
  dataType: "email" | "phone",
  lang: string
): string => {
  const positionName = position.includes("vat_refund")
    ? "MVA-refusjonstjenesten"
    : "tredjepart";
  const dataTypeName =
    dataType === "email" ? "e-postadressen" : "telefonnummeret";

  if (lang === "nor") {
    return `Merk: Ved å klikke på knappen vil ${dataTypeName} ditt deles med ${positionName}.`;
  }

  const dataTypeNameEng =
    dataType === "email" ? "email address" : "phone number";
  const positionNameEng = position.includes("vat_refund")
    ? "the VAT refund service provider"
    : "the third-party service provider";
  return `Note: Clicking the button will share your ${dataTypeNameEng} with ${positionNameEng}.`;
};

export const aggregateArticles = (
  receipt: Receipt
): ExtendedServicePayload["articles"] => {
  const articlesMap = new Map<string, number>();

  receipt.articles?.forEach((art) => {
    if (art.art_number && art.art_final_price !== undefined) {
      const currentTotal = articlesMap.get(art.art_number) ?? 0;
      articlesMap.set(art.art_number, currentTotal + art.art_final_price);
    }
  });

  return Array.from(articlesMap.entries()).map(
    ([art_number, art_final_price]) => ({ art_number, art_final_price })
  );
};

export const categorizeServices = (items: ExtendedViewItem[]) => {
  const receiptServices: ExtendedViewItem[] = [];
  const merchantOffers: ExtendedViewItem[] = [];

  items.forEach((item) => {
    if (
      ["customer_club", "website", "help_desk", "return_service"].includes(
        item.extended_view_position
      )
    ) {
      merchantOffers.push(item);
    } else {
      receiptServices.push(item);
    }
  });

  return { receiptServices, merchantOffers };
};

